import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Route, ChevronRight, Lock, CheckCircle2, Circle, PlayCircle, PauseCircle, MinusCircle, ArrowRight } from "lucide-react";
import { api, formatApiError } from "../api/client";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

export const STEP_STATUS = {
  locked: { label: "Bloqueado", cls: "bg-slate-100 text-slate-400", Icon: Lock },
  available: { label: "Disponible", cls: "bg-sky-100 text-sky-700 border-sky-200", Icon: Circle },
  in_progress: { label: "En progreso", cls: "bg-amber-100 text-amber-800 border-amber-200", Icon: PlayCircle },
  completed: { label: "Completado", cls: "bg-emerald-100 text-emerald-800 border-emerald-200", Icon: CheckCircle2 },
  postponed: { label: "Aplazado", cls: "bg-slate-100 text-slate-500", Icon: PauseCircle },
  not_applicable: { label: "No aplica", cls: "bg-slate-100 text-slate-400", Icon: MinusCircle },
};

function StatusBadge({ status }) {
  const s = STEP_STATUS[status] || STEP_STATUS.available;
  const { Icon } = s;
  return (
    <Badge className={`${s.cls} gap-1`} data-testid={`step-status-${status}`}>
      <Icon className="h-3 w-3" /> {s.label}
    </Badge>
  );
}

export default function Journey() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [next, setNext] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/journey?framework=iso27001");
        setData(data);
        try { const r = await api.get("/journey/next?framework=iso27001"); setNext(r.data); } catch { /* opcional */ }
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail));
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="text-slate-500">Cargando la ruta...</div>;
  if (!data) return <div className="text-slate-500">No se pudo cargar la ruta de implementación.</div>;

  const { template, progress } = data;
  const stateByCode = Object.fromEntries((progress.step_states || []).map((s) => [s.step_code, s]));

  return (
    <div data-testid="journey-page" className="max-w-5xl">
      <div className="flex items-center gap-2">
        <Route className="h-6 w-6 text-primary" />
        <div>
          <h1 className="text-[20px] font-semibold tracking-tight text-slate-900">Ruta de implementación</h1>
          <p className="text-[14px] text-slate-500 mt-0.5">{template.name}</p>
        </div>
      </div>

      <div className="mt-6 bg-white border border-slate-200 rounded-md p-5">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[13px] font-medium text-slate-700">Progreso global</span>
          <span className="text-[20px] font-semibold text-slate-900" data-testid="journey-global-progress">{progress.progress_global}%</span>
        </div>
        <div className="h-2 bg-slate-100 rounded overflow-hidden">
          <div className="h-2 bg-primary rounded transition-all" style={{ width: `${progress.progress_global}%` }} />
        </div>
        {next?.step && (
          <div className="mt-4 flex items-center justify-between bg-primary/5 border border-primary/20 rounded-md px-4 py-3">
            <div>
              <div className="text-[12px] uppercase tracking-wide text-primary font-medium">Tu siguiente paso</div>
              <div className="text-[14px] text-slate-900 font-medium mt-0.5">
                {next.step.human_title || next.step.title}
                {next.step.guidance_structured?.estimated_effort && (
                  <span className="text-[12px] text-slate-500 font-normal"> · ~{next.step.guidance_structured.estimated_effort}</span>
                )}
              </div>
            </div>
            <Button size="sm" onClick={() => navigate(`/ruta/pasos/${next.step.code}`)} data-testid="journey-next-cta">
              Continuar <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        )}
      </div>

      <div className="mt-6 space-y-5">
        {template.phases.map((ph) => (
          <div key={ph.code} className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid={`phase-${ph.code}`}>
            <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between bg-slate-50/60">
              <div>
                <div className="text-[14px] font-semibold text-slate-900">{ph.code} · {ph.title}</div>
                <div className="text-[12px] text-slate-500 mt-0.5">{ph.description}</div>
              </div>
              <div className="text-right">
                <div className="text-[16px] font-semibold text-slate-900">{progress.progress_by_phase?.[ph.code] ?? 0}%</div>
              </div>
            </div>
            <div className="divide-y divide-slate-100">
              {ph.steps.map((st) => {
                const state = stateByCode[st.code] || {};
                return (
                  <button
                    key={st.code}
                    onClick={() => navigate(`/ruta/pasos/${st.code}`)}
                    className="w-full flex items-center gap-3 px-5 py-3 hover:bg-slate-50 text-left transition-colors"
                    data-testid={`step-row-${st.code}`}
                  >
                    <span className="text-[12px] font-mono text-slate-400 w-8">{st.code}</span>
                    <span className="flex-1 text-[14px] text-slate-800">{st.human_title || st.title}</span>
                    {(state.linked_document_ids?.length > 0) && (
                      <span className="text-[11px] text-slate-400">{state.linked_document_ids.length} doc.</span>
                    )}
                    <StatusBadge status={state.status} />
                    <ChevronRight className="h-4 w-4 text-slate-300" />
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
